import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { useToast } from '../../components/ToastProvider';

type Platform = 'instagram' | 'facebook' | 'tiktok' | 'linkedin' | 'twitter' | 'youtube' | 'pinterest' | 'threads';
type VisualStyle = 'professional' | 'modern' | 'minimalist' | 'colorful' | 'elegant';
type ImageSize = '1024x1024' | '1024x1536' | '1536x1024';

const PLATFORMS = [
  { id: 'instagram' as Platform, name: 'Instagram', icon: 'fa-brands fa-instagram' },
  { id: 'facebook' as Platform, name: 'Facebook', icon: 'fa-brands fa-facebook' },
  { id: 'tiktok' as Platform, name: 'TikTok', icon: 'fa-brands fa-tiktok' },
  { id: 'linkedin' as Platform, name: 'LinkedIn', icon: 'fa-brands fa-linkedin' },
  { id: 'twitter' as Platform, name: 'Twitter/X', icon: 'fa-brands fa-x-twitter' },
  { id: 'youtube' as Platform, name: 'YouTube', icon: 'fa-brands fa-youtube' },
  { id: 'pinterest' as Platform, name: 'Pinterest', icon: 'fa-brands fa-pinterest' },
  { id: 'threads' as Platform, name: 'Threads', icon: 'fa-brands fa-threads' },
];

interface PostContent {
  hook?: string;
  body: string;
  cta?: string;
  hashtags?: string[];
}

interface SocialPost {
  _id: string;
  platform: Platform;
  content: PostContent;
  imageUrls?: string[];
  imageSize?: ImageSize;
  imageCount?: number;
  visualStyle?: VisualStyle;
  aiGenerated: boolean;
  edited: boolean;
  createdAt: string;
}

interface ConnectedAccount {
  _id: string;
  platform: 'instagram' | 'facebook';
  accountId: string;
  accountName: string;
  accountUsername?: string;
  profilePictureUrl?: string;
  isActive: boolean;
  createdAt: string;
}

export function SocialPostsPage() {
  const { token, user } = useAuth();
  const { push } = useToast();

  // Mode selection
  const [mode, setMode] = useState<'generate' | 'myImage'>('generate');

  // MyImage sub-mode
  const [myImageAction, setMyImageAction] = useState<'createPost' | 'editFirst' | 'editAndCreatePost'>('createPost');

  // Form state (generate mode)
  const [platform, setPlatform] = useState<Platform>('instagram');
  const [objective, setObjective] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [keyOffer, setKeyOffer] = useState('');
  const [generateImage, setGenerateImage] = useState(true);
  const [visualStyle, setVisualStyle] = useState<VisualStyle>('modern');
  const [imageSize, setImageSize] = useState<ImageSize | 'auto'>('1024x1024');
  const [imageCount, setImageCount] = useState(1);

  // MyImage mode state
  const [uploadedImage, setUploadedImage] = useState<string>(''); // base64 ou URL
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Generated/edited results
  const [generatedPost, setGeneratedPost] = useState<SocialPost | null>(null);
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Modo avançado - publicação automática
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  // Removido sistema de créditos - usa subscription

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const data = await api.generateSocialPost(
        {
          platform,
          objective,
          audience: '', // Removido do formulário, IA decide automaticamente
          brandVoice,
          keyOffer,
          generateImage,
          visualStyle,
          imageSize,
          imageCount
        },
        token
      );

      setGeneratedPost(data.post);
      setCurrentImageIndex(0); // Reset para primeira imagem
    } catch (err: any) {
      console.error('Erro ao gerar post:', err);
      setError(err.message || 'Erro ao gerar post');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateImage = async () => {
    if (!generatedPost || !token) return;

    setGeneratingImage(true);
    setError(null);

    try {
      const data = await api.regenerateSocialPostImage(
        generatedPost._id,
        visualStyle,
        token
      );

      setGeneratedPost({
        ...generatedPost,
        imageUrls: data.imageUrls
      });

      setCurrentImageIndex(0); // Reset para primeira imagem
    } catch (err: any) {
      console.error('Erro ao gerar imagem:', err);
      setError(err.message || 'Erro ao gerar imagem');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedPost?.imageUrls || generatedPost.imageUrls.length === 0) return;

    // Download da imagem atual
    const currentImageUrl = generatedPost.imageUrls[currentImageIndex];
    const link = document.createElement('a');
    link.href = currentImageUrl;
    link.download = `post_${platform}_${currentImageIndex + 1}_${Date.now()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione apenas arquivos de imagem');
      return;
    }

    if (file.size > 25 * 1024 * 1024) { // 25MB
      setError('Imagem muito grande. Máximo 25MB');
      return;
    }

    setImageFile(file);
    setError(null);

    // Criar preview em base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Editar imagem
  const handleEditImage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !uploadedImage || !editPrompt) return;

    setLoading(true);
    setError(null);
    setEditedImageUrl(null);

    try {
      const data = await api.editSocialImage(
        {
          imageUrl: uploadedImage, // Usa a imagem carregada (base64 ou URL)
          editPrompt,
          imageSize: imageSize as string
        },
        token
      );

      setEditedImageUrl(data.editedImageUrl);

      // Se editou com sucesso e quer criar post depois, atualizar uploaded image
      if (myImageAction === 'editFirst') {
        setUploadedImage(data.editedImageUrl);
        push('Imagem editada! Agora você pode criar o post.', 'success');
      }
    } catch (err: any) {
      console.error('Erro ao editar imagem:', err);
      setError(err.message || 'Erro ao editar imagem');
    } finally {
      setLoading(false);
    }
  };

  // Criar post a partir da imagem
  const handleCreatePostFromImage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !uploadedImage) return;

    setLoading(true);
    setError(null);

    try {
      const imageToUse = editedImageUrl || uploadedImage; // Usa editada se existir

      const data = await api.createPostFromImage(
        {
          imageBase64: imageToUse,
          platform,
          additionalContext,
          brandVoice,
          keyOffer,
          imageSize: '1024x1024'
        },
        token
      );

      setGeneratedPost(data.post);
      setCurrentImageIndex(0);
      push('Post criado com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao criar post:', err);
      setError(err.message || 'Erro ao criar post da imagem');
    } finally {
      setLoading(false);
    }
  };

  // Editar imagem E criar post automaticamente
  const handleEditAndCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !uploadedImage || !editPrompt) return;

    setLoading(true);
    setError(null);
    setEditedImageUrl(null);

    try {
      // Primeiro: Editar a imagem
      const editData = await api.editSocialImage(
        {
          imageUrl: uploadedImage,
          editPrompt,
          imageSize: imageSize as string
        },
        token
      );

      const editedImage = editData.editedImageUrl;
      setEditedImageUrl(editedImage);

      // Segundo: Criar post a partir da imagem editada
      const postData = await api.createPostFromImage(
        {
          imageBase64: editedImage,
          platform,
          additionalContext,
          brandVoice,
          keyOffer,
          imageSize: '1024x1024'
        },
        token
      );

      setGeneratedPost(postData.post);
      setCurrentImageIndex(0);
      push('Imagem editada e post criado com sucesso!', 'success');
    } catch (err: any) {
      console.error('Erro ao editar e criar post:', err);
      setError(err.message || 'Erro ao editar imagem e criar post');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadEditedImage = () => {
    if (!editedImageUrl) return;

    const link = document.createElement('a');
    link.href = editedImageUrl;
    link.download = `edited_image_${Date.now()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFormattedText = (): string => {
    if (!generatedPost) return '';

    const parts: string[] = [];
    if (generatedPost.content.hook) parts.push(generatedPost.content.hook);
    if (generatedPost.content.body) parts.push('', generatedPost.content.body);
    if (generatedPost.content.cta) parts.push('', generatedPost.content.cta);
    if (generatedPost.content.hashtags?.length) {
      parts.push('', generatedPost.content.hashtags.join(' '));
    }

    return parts.join('\n');
  };

  const handleCopyText = () => {
    const text = getFormattedText();
    navigator.clipboard.writeText(text);
    push('Texto copiado!', 'success');
  };

  // Carregar contas conectadas ao montar
  useEffect(() => {
    loadConnectedAccounts();
  }, []);

  const loadConnectedAccounts = async () => {
    if (!token) return;

    try {
      const data = await api.listConnectedAccounts(token);
      setConnectedAccounts(data.accounts as ConnectedAccount[]);
    } catch (error: any) {
      // Silencioso - se não tem contas, não mostra erro
      console.log('Modo avançado não ativo ou sem contas');
    }
  };

  const handlePublishAutomatically = () => {
    if (connectedAccounts.length === 0) {
      push('Conecte uma conta Meta nas Configurações primeiro', 'error');
      return;
    }

    if (!generatedPost || !generatedPost.imageUrls || generatedPost.imageUrls.length === 0) {
      push('Gere um post com imagem primeiro', 'error');
      return;
    }

    // Filtrar contas compatíveis com a plataforma
    const compatibleAccounts = connectedAccounts.filter(acc => acc.platform === platform);

    if (compatibleAccounts.length === 0) {
      push(`Nenhuma conta ${platform === 'instagram' ? 'Instagram' : 'Facebook'} conectada`, 'error');
      return;
    }

    if (compatibleAccounts.length === 1) {
      setSelectedAccountId(compatibleAccounts[0]._id);
    }

    setShowPublishModal(true);
  };

  const handlePublish = async () => {
    if (!selectedAccountId || !generatedPost || !token) return;

    try {
      setPublishing(true);

      const caption = getFormattedText();

      const data = await api.publishToSocial(
        selectedAccountId,
        generatedPost.imageUrls!,
        caption,
        token
      );

      push(`Publicado com sucesso no ${data.accountName}!`, 'success');
      setShowPublishModal(false);
    } catch (error: any) {
      push(error.message || 'Erro ao publicar', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const disableSubmit = loading || !objective;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2 heading">Posts para Redes Sociais</h1>
        <p className="text-dim">Crie posts incríveis para Instagram e Facebook com IA</p>
        {user?.subscription && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-brand-500/10 border border-brand-500/20 text-sm">
            <i className="fa-solid fa-crown text-brand-400"></i>
            <span>Plano {user.subscription.planName}</span>
          </div>
        )}
      </div>

      {/* Mode Tabs */}
      <div className="panel p-1 inline-flex rounded-lg" style={{ backgroundColor: 'var(--cg-bg-alt)' }}>
        <button
          type="button"
          onClick={() => setMode('generate')}
          className={`px-4 py-2 rounded-md transition-all text-sm font-medium ${
            mode === 'generate'
              ? 'shadow-sm'
              : ''
          }`}
          style={mode === 'generate' ? {
            backgroundColor: 'var(--cg-surface)',
            color: 'var(--cg-text)'
          } : {
            color: 'var(--cg-text-dim)'
          }}
        >
          <i className="fa-solid fa-wand-magic-sparkles mr-1"></i> Gerar Nova
        </button>
        <button
          type="button"
          onClick={() => setMode('myImage')}
          className={`px-4 py-2 rounded-md transition-all text-sm font-medium ${
            mode === 'myImage'
              ? 'shadow-sm'
              : ''
          }`}
          style={mode === 'myImage' ? {
            backgroundColor: 'var(--cg-surface)',
            color: 'var(--cg-text)'
          } : {
            color: 'var(--cg-text-dim)'
          }}
        >
          <i className="fa-solid fa-image mr-1"></i> Minha Imagem
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <div className="panel p-6">
          <h2 className="text-lg font-semibold mb-4 heading">
            {mode === 'generate' ? 'Configurar Post' : 'Trabalhar com Minha Imagem'}
          </h2>

          {mode === 'generate' ? (
          <form onSubmit={handleGenerate} className="space-y-4">
            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Plataforma *</label>
              <div className="grid grid-cols-4 gap-2">
                {PLATFORMS.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlatform(p.id)}
                    className={`p-2.5 rounded-lg border-2 transition-all text-center ${
                      platform === p.id
                        ? 'border-brand-500 bg-brand-500/10'
                        : 'border-[var(--cg-border)] hover:border-[var(--cg-border-strong)] bg-[var(--cg-surface)]'
                    }`}
                    style={{ color: 'var(--cg-text)' }}
                  >
                    <div className="text-lg mb-0.5"><i className={p.icon}></i></div>
                    <div className="text-[10px] font-medium truncate">{p.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Conteúdo Principal - Simplificado */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <i className="fa-solid fa-pen-to-square mr-1"></i> Sobre o que é o post? *
              </label>
              <textarea
                value={objective}
                onChange={e => setObjective(e.target.value)}
                placeholder="Ex: Novo produto de skincare para pele oleosa, promoção de Black Friday, dica de treino para iniciantes..."
                className="input min-h-[100px]"
                required
              />
              <p className="text-xs mt-1 text-dim">
                Descreva brevemente o assunto do post. A IA vai criar o texto completo!
              </p>
            </div>

            {/* Tom - Simplificado */}
            <div>
              <label className="block text-sm font-medium mb-2"><i className="fa-solid fa-masks-theater mr-1"></i> Tom do Post</label>
              <select
                value={brandVoice}
                onChange={e => setBrandVoice(e.target.value)}
                className="input"
              >
                <option value="">Automático (IA decide)</option>
                <option value="profissional">Profissional</option>
                <option value="casual">Casual/Descontraído</option>
                <option value="divertido">Divertido/Engraçado</option>
                <option value="inspirador">Inspirador/Motivacional</option>
                <option value="educativo">Educativo/Informativo</option>
              </select>
            </div>

            {/* Destaque Opcional */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <i className="fa-solid fa-bullseye mr-1"></i> Destaque Especial (opcional)
              </label>
              <input
                type="text"
                value={keyOffer}
                onChange={e => setKeyOffer(e.target.value)}
                placeholder="Ex: 30% OFF, Frete Grátis, Lançamento Exclusivo..."
                className="input"
              />
              <p className="text-xs mt-1 text-dim">
                Alguma promoção, desconto ou informação importante?
              </p>
            </div>

            {/* Image Options */}
            <div className="border-t pt-4" style={{ borderColor: 'var(--cg-border)' }}>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">Gerar Imagem com IA</label>
                <button
                  type="button"
                  onClick={() => setGenerateImage(!generateImage)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    generateImage ? 'bg-[var(--cg-accent)]' : 'bg-[var(--cg-border)]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      generateImage ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {generateImage && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Estilo Visual</label>
                    <div className="grid grid-cols-5 gap-2">
                      {[
                        { id: 'modern', icon: 'fa-solid fa-palette', name: 'Moderno' },
                        { id: 'professional', icon: 'fa-solid fa-briefcase', name: 'Profissional' },
                        { id: 'minimalist', icon: 'fa-regular fa-square', name: 'Minimalista' },
                        { id: 'colorful', icon: 'fa-solid fa-rainbow', name: 'Colorido' },
                        { id: 'elegant', icon: 'fa-solid fa-gem', name: 'Elegante' },
                      ].map(style => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setVisualStyle(style.id as VisualStyle)}
                          className={`p-2 rounded-lg border-2 transition-all text-center ${
                            visualStyle === style.id
                              ? 'border-brand-500 bg-brand-500/10'
                              : 'border-[var(--cg-border)] hover:border-[var(--cg-border-strong)] bg-[var(--cg-surface)]'
                          }`}
                        >
                          <div className="text-base"><i className={style.icon}></i></div>
                          <div className="text-[9px] font-medium mt-0.5">{style.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Formato da Imagem</label>
                    <select
                      value={imageSize}
                      onChange={e => setImageSize(e.target.value as ImageSize)}
                      className="input"
                    >
                      <option value="1024x1024">Quadrado (1:1) - Feed</option>
                      <option value="1024x1536">Vertical (2:3) - Stories/Reels</option>
                      <option value="1536x1024">Paisagem (3:2) - Horizontal</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Quantidade de Imagens {imageCount > 1 && `(${imageCount} imagens = +${imageCount} créditos)`}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={imageCount}
                        onChange={e => setImageCount(Number(e.target.value))}
                        className="flex-1"
                        style={{ accentColor: 'var(--cg-accent)' }}
                      />
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={imageCount}
                        onChange={e => setImageCount(Math.min(10, Math.max(1, Number(e.target.value))))}
                        className="input w-20 text-center"
                      />
                    </div>
                    <p className="text-xs mt-1 text-dim">
                      {imageCount === 1 ? 'Post simples' : `Carrossel com ${imageCount} imagens`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-lg text-sm" style={{
                backgroundColor: 'var(--cg-danger-bg)',
                color: 'var(--cg-danger)',
                border: '1px solid var(--cg-danger)'
              }}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={disableSubmit}
              className="btn btn-primary w-full h-11"
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  Gerando...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  Gerar Post
                </>
              )}
            </button>
          </form>
          ) : (
          <div className="space-y-4">
            {/* Drag & Drop Area */}
            <div>
              <label className="block text-sm font-medium mb-2">Sua Imagem *</label>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className="relative border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer"
                style={{
                  borderColor: isDragging ? 'var(--cg-accent)' : 'var(--cg-border)',
                  backgroundColor: isDragging ? 'var(--cg-accent-soft)' : 'var(--cg-bg-alt)'
                }}
                onClick={() => document.getElementById('myImageFileInput')?.click()}
              >
                {!uploadedImage ? (
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 mb-3" style={{ color: 'var(--cg-text-dim)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="font-medium" style={{ color: 'var(--cg-text)' }}>
                      Arraste sua imagem ou clique para selecionar
                    </p>
                    <p className="text-xs mt-1 text-dim">
                      PNG, JPG, WEBP até 25MB
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={editedImageUrl || uploadedImage}
                      alt="Sua imagem"
                      className="w-full rounded-lg"
                      style={{ boxShadow: 'var(--cg-shadow)' }}
                    />
                    {editedImageUrl && (
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-md text-xs font-medium bg-green-500 text-white">
                        <i className="fa-solid fa-check mr-1"></i> Editada
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedImage('');
                        setEditedImageUrl(null);
                        setImageFile(null);
                      }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: 'var(--cg-danger)',
                        color: 'white'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                <input
                  id="myImageFileInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
              </div>
            </div>

            {uploadedImage && (
              <>
                {/* Action Selection - Improved UX */}
                <div>
                  <label className="block text-sm font-medium mb-3">Escolha uma ação</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Criar Post */}
                    <label
                      className="relative p-4 rounded-xl border-2 cursor-pointer transition-all group hover:shadow-md"
                      style={{
                        borderColor: myImageAction === 'createPost' ? 'var(--cg-accent)' : 'var(--cg-border)',
                        backgroundColor: myImageAction === 'createPost' ? 'var(--cg-accent-soft)' : 'var(--cg-surface)'
                      }}
                    >
                      <input
                        type="radio"
                        name="action"
                        value="createPost"
                        checked={myImageAction === 'createPost'}
                        onChange={(e) => setMyImageAction(e.target.value as any)}
                        className="sr-only"
                      />
                      <div className="flex flex-col items-center text-center gap-2">
                        <div className="text-3xl"><i className="fa-solid fa-sparkles"></i></div>
                        <div className="font-semibold text-sm">Criar Post</div>
                        <div className="text-xs text-dim">Gera texto com IA</div>
                      </div>
                      {myImageAction === 'createPost' && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--cg-accent)', color: 'white' }}>
                          <i className="fa-solid fa-check text-xs"></i>
                        </div>
                      )}
                    </label>

                    {/* Editar Imagem */}
                    <label
                      className="relative p-4 rounded-xl border-2 cursor-pointer transition-all group hover:shadow-md"
                      style={{
                        borderColor: myImageAction === 'editFirst' ? 'var(--cg-accent)' : 'var(--cg-border)',
                        backgroundColor: myImageAction === 'editFirst' ? 'var(--cg-accent-soft)' : 'var(--cg-surface)'
                      }}
                    >
                      <input
                        type="radio"
                        name="action"
                        value="editFirst"
                        checked={myImageAction === 'editFirst'}
                        onChange={(e) => setMyImageAction(e.target.value as any)}
                        className="sr-only"
                      />
                      <div className="flex flex-col items-center text-center gap-2">
                        <div className="text-3xl"><i className="fa-solid fa-palette"></i></div>
                        <div className="font-semibold text-sm">Editar Imagem</div>
                        <div className="text-xs text-dim">Apenas edição</div>
                      </div>
                      {myImageAction === 'editFirst' && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--cg-accent)', color: 'white' }}>
                          <i className="fa-solid fa-check text-xs"></i>
                        </div>
                      )}
                    </label>

                    {/* Editar e Criar Post */}
                    <label
                      className="relative p-4 rounded-xl border-2 cursor-pointer transition-all group hover:shadow-md"
                      style={{
                        borderColor: myImageAction === 'editAndCreatePost' ? 'var(--cg-accent)' : 'var(--cg-border)',
                        backgroundColor: myImageAction === 'editAndCreatePost' ? 'var(--cg-accent-soft)' : 'var(--cg-surface)'
                      }}
                    >
                      <input
                        type="radio"
                        name="action"
                        value="editAndCreatePost"
                        checked={myImageAction === 'editAndCreatePost'}
                        onChange={(e) => setMyImageAction(e.target.value as any)}
                        className="sr-only"
                      />
                      <div className="flex flex-col items-center text-center gap-2">
                        <div className="text-3xl"><i className="fa-solid fa-palette"></i><i className="fa-solid fa-sparkles ml-1"></i></div>
                        <div className="font-semibold text-sm">Editar + Post</div>
                        <div className="text-xs text-dim">Completo</div>
                      </div>
                      {myImageAction === 'editAndCreatePost' && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--cg-accent)', color: 'white' }}>
                          <i className="fa-solid fa-check text-xs"></i>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Platform Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">Plataforma *</label>
                  <div className="grid grid-cols-4 gap-2">
                    {PLATFORMS.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlatform(p.id)}
                        className={`p-2.5 rounded-lg border-2 transition-all text-center ${
                          platform === p.id
                            ? 'border-brand-500 bg-brand-500/10'
                            : 'border-[var(--cg-border)] hover:border-[var(--cg-border-strong)] bg-[var(--cg-surface)]'
                        }`}
                        style={{ color: 'var(--cg-text)' }}
                      >
                        <div className="text-lg mb-0.5"><i className={p.icon}></i></div>
                        <div className="text-[10px] font-medium truncate">{p.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Conditional Fields based on Action */}

                {/* Edit Prompt (for editFirst and editAndCreatePost) */}
                {(myImageAction === 'editFirst' || myImageAction === 'editAndCreatePost') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">O que você quer mudar? *</label>
                      <textarea
                        value={editPrompt}
                        onChange={e => setEditPrompt(e.target.value)}
                        placeholder="Ex: Trocar o céu para noite estrelada, mudar a cor do vestido para vermelho..."
                        className="input min-h-[100px]"
                        required
                      />
                      <p className="text-xs mt-1 text-dim">
                        Descreva as mudanças que você quer fazer
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Tamanho da Imagem</label>
                      <select
                        value={imageSize}
                        onChange={e => setImageSize(e.target.value as any)}
                        className="input"
                      >
                        <option value="auto">Auto (manter proporções originais)</option>
                        <option value="1024x1024">Quadrado (1:1) - Feed</option>
                        <option value="1024x1536">Vertical (2:3) - Stories/Reels</option>
                        <option value="1536x1024">Paisagem (3:2) - Horizontal</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Post Creation Fields (for createPost and editAndCreatePost) */}
                {(myImageAction === 'createPost' || myImageAction === 'editAndCreatePost') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <i className="fa-solid fa-comment mr-1"></i> Instruções para o Post (Opcional)
                      </label>
                      <textarea
                        value={additionalContext}
                        onChange={e => setAdditionalContext(e.target.value)}
                        placeholder="Ex: Tom profissional, público 20-30 anos, destacar promoção de 30% OFF..."
                        className="input min-h-[90px]"
                      />
                      <p className="text-xs mt-1 text-dim">
                        {myImageAction === 'editAndCreatePost'
                          ? 'A IA analisa a imagem EDITADA e gera o texto do post'
                          : 'A IA analisa a imagem e gera o texto automaticamente'}
                      </p>
                    </div>
                  </>
                )}

                {/* Error Message */}
                {error && (
                  <div className="p-3 rounded-lg text-sm" style={{
                    backgroundColor: 'var(--cg-danger-bg)',
                    color: 'var(--cg-danger)',
                    border: '1px solid var(--cg-danger)'
                  }}>
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={
                    myImageAction === 'createPost'
                      ? handleCreatePostFromImage
                      : myImageAction === 'editAndCreatePost'
                      ? handleEditAndCreatePost
                      : handleEditImage
                  }
                  disabled={loading || ((myImageAction === 'editFirst' || myImageAction === 'editAndCreatePost') && !editPrompt)}
                  className="btn btn-primary w-full h-11"
                >
                  {loading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      Processando...
                    </>
                  ) : myImageAction === 'createPost' ? (
                    <>
                      <i className="fa-solid fa-sparkles"></i>
                      Criar Post com IA
                    </>
                  ) : myImageAction === 'editAndCreatePost' ? (
                    <>
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                      <i className="fa-solid fa-sparkles ml-1"></i>
                      Editar e Criar Post
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                      Editar Imagem com IA
                    </>
                  )}
                </button>
              </>
            )}
          </div>
          )}
        </div>

        {/* Preview Section */}
        <div className="panel p-6">
          <h2 className="text-lg font-semibold mb-4 heading">
            {mode === 'generate' ? 'Preview do Post' : 'Resultado da Edição'}
          </h2>

          {mode === 'myImage' ? (
            !editedImageUrl ? (
              <div className="flex items-center justify-center h-96 rounded-lg border-2 border-dashed"
                style={{ borderColor: 'var(--cg-border)', backgroundColor: 'var(--cg-bg-alt)' }}>
                <div className="text-center text-dim">
                  <svg className="mx-auto h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>Cole a URL e descreva as mudanças</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4 fade-in">
                <div className="relative">
                  <img
                    src={editedImageUrl}
                    alt="Imagem editada"
                    className="w-full rounded-lg"
                    style={{ boxShadow: 'var(--cg-shadow)' }}
                  />
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={handleDownloadEditedImage}
                      className="px-3 py-2 text-sm font-medium rounded-md transition-all shadow-lg"
                      style={{
                        backgroundColor: 'var(--cg-surface)',
                        color: 'var(--cg-text)',
                        border: '1px solid var(--cg-border-strong)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-bg-alt)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-surface)'}
                    >
                      <i className="fa-solid fa-download mr-1"></i> Download
                    </button>
                  </div>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cg-bg-alt)' }}>
                  <div className="text-xs font-semibold mb-1 text-dim">MUDANÇAS APLICADAS</div>
                  <div style={{ color: 'var(--cg-text)' }}>{editPrompt}</div>
                </div>
              </div>
            )
          ) : (
          !generatedPost ? (
            <div className="flex items-center justify-center h-96 rounded-lg border-2 border-dashed"
              style={{ borderColor: 'var(--cg-border)', backgroundColor: 'var(--cg-bg-alt)' }}>
              <div className="text-center text-dim">
                <svg className="mx-auto h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p>Preencha o formulário e gere seu post</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 fade-in">
              {/* Image Preview - Carrossel */}
              {generatedPost.imageUrls && generatedPost.imageUrls.length > 0 && (
                <div className="relative">
                  <img
                    src={generatedPost.imageUrls[currentImageIndex]}
                    alt={`Post preview ${currentImageIndex + 1}`}
                    className="w-full rounded-lg"
                    style={{ boxShadow: 'var(--cg-shadow)' }}
                  />

                  {/* Botões de ação */}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={handleDownloadImage}
                      className="px-3 py-2 text-sm font-medium rounded-md transition-all shadow-lg"
                      style={{
                        backgroundColor: 'var(--cg-surface)',
                        color: 'var(--cg-text)',
                        border: '1px solid var(--cg-border-strong)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-bg-alt)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-surface)'}
                    >
                      <i className="fa-solid fa-download mr-1"></i> Download
                    </button>
                    <button
                      onClick={handleRegenerateImage}
                      disabled={generatingImage}
                      className="px-3 py-2 text-sm font-medium rounded-md transition-all shadow-lg disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--cg-accent)',
                        color: '#ffffff',
                        border: 'none'
                      }}
                      onMouseEnter={(e) => !generatingImage && (e.currentTarget.style.opacity = '0.9')}
                      onMouseLeave={(e) => !generatingImage && (e.currentTarget.style.opacity = '1')}
                    >
                      {generatingImage ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-rotate"></i>} Regerar
                    </button>
                  </div>

                  {/* Navegação do carrossel */}
                  {generatedPost.imageUrls.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? generatedPost.imageUrls!.length - 1 : prev - 1))}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all"
                        style={{ backgroundColor: 'var(--cg-surface)', color: 'var(--cg-text)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-bg-alt)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-surface)'}
                      >
                        ‹
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev === generatedPost.imageUrls!.length - 1 ? 0 : prev + 1))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all"
                        style={{ backgroundColor: 'var(--cg-surface)', color: 'var(--cg-text)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-bg-alt)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cg-surface)'}
                      >
                        ›
                      </button>

                      {/* Indicadores */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                        {generatedPost.imageUrls.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className="w-2 h-2 rounded-full transition-all"
                            style={{
                              backgroundColor: idx === currentImageIndex ? 'var(--cg-accent)' : 'rgba(255,255,255,0.6)'
                            }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Text Content */}
              <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--cg-bg-alt)' }}>
                {generatedPost.content.hook && (
                  <div>
                    <div className="text-xs font-semibold mb-1 text-dim">HOOK</div>
                    <div className="text-lg font-bold heading">{generatedPost.content.hook}</div>
                  </div>
                )}

                <div>
                  <div className="text-xs font-semibold mb-1 text-dim">CORPO</div>
                  <div className="whitespace-pre-wrap" style={{ color: 'var(--cg-text)' }}>{generatedPost.content.body}</div>
                </div>

                {generatedPost.content.cta && (
                  <div>
                    <div className="text-xs font-semibold mb-1 text-dim">CTA</div>
                    <div className="font-medium" style={{ color: 'var(--cg-accent)' }}>{generatedPost.content.cta}</div>
                  </div>
                )}

                {generatedPost.content.hashtags && generatedPost.content.hashtags.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold mb-1 text-dim">HASHTAGS</div>
                    <div style={{ color: 'var(--cg-accent)' }}>{generatedPost.content.hashtags.join(' ')}</div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  onClick={handleCopyText}
                  className="btn w-full h-10"
                  style={{
                    backgroundColor: 'var(--cg-success)',
                    color: 'white',
                    fontWeight: 600
                  }}
                >
                  <i className="fa-solid fa-copy"></i>
                  Copiar Texto
                </button>

                {/* Botão de Publicação Automática - só aparece se tem contas conectadas */}
                {connectedAccounts.length > 0 && generatedPost?.imageUrls && generatedPost.imageUrls.length > 0 && (
                  <button
                    onClick={handlePublishAutomatically}
                    className="btn btn-primary w-full h-10"
                  >
                    <i className="fa-solid fa-rocket mr-2"></i>
                    Publicar Automaticamente
                  </button>
                )}
              </div>

              {/* Instructions */}
              <div className="rounded-lg p-3 text-sm"
                style={{
                  backgroundColor: 'var(--cg-accent-soft)',
                  border: '1px solid var(--cg-border)',
                  color: 'var(--cg-text)'
                }}>
                <strong className="heading">
                  {connectedAccounts.length > 0 ? 'Publicação:' : 'Próximos passos:'}
                </strong>
                {connectedAccounts.length > 0 ? (
                  <p className="mt-2 text-dim">
                    Modo avançado ativo! Você pode publicar diretamente ou copiar manualmente.
                  </p>
                ) : (
                  <ol className="mt-2 space-y-1 list-decimal list-inside text-dim">
                    <li>Baixe a imagem (se gerada)</li>
                    <li>Copie o texto</li>
                    <li>Abra {platform === 'instagram' ? 'Instagram' : 'Facebook'}</li>
                    <li>Crie um novo post e cole o conteúdo</li>
                  </ol>
                )}
              </div>
            </div>
          )
        )}
        </div>
      </div>

      {/* Modal de Publicação */}
      {showPublishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
          <div className="panel max-w-md w-full p-6">
            <h2 className="text-xl font-bold heading mb-4">Publicar Automaticamente</h2>

            <div className="space-y-4">
              <p className="text-sm text-dim">
                Selecione em qual conta você deseja publicar:
              </p>

              <div className="space-y-2">
                {connectedAccounts
                  .filter(acc => acc.platform === platform)
                  .map(account => (
                    <label
                      key={account._id}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                      style={{
                        backgroundColor: selectedAccountId === account._id ? 'var(--cg-accent-soft)' : 'var(--cg-bg-alt)',
                        border: selectedAccountId === account._id ? '2px solid var(--cg-accent)' : '2px solid transparent'
                      }}
                    >
                      <input
                        type="radio"
                        name="account"
                        value={account._id}
                        checked={selectedAccountId === account._id}
                        onChange={e => setSelectedAccountId(e.target.value)}
                        className="text-accent"
                      />
                      {account.profilePictureUrl && (
                        <img
                          src={account.profilePictureUrl}
                          alt={account.accountName}
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <div className="flex-1">
                        <div className="font-semibold heading text-sm">{account.accountName}</div>
                        {account.accountUsername && (
                          <div className="text-xs text-dim">@{account.accountUsername}</div>
                        )}
                      </div>
                    </label>
                  ))}
              </div>

              {generatedPost?.imageUrls && generatedPost.imageUrls.length > 1 && (
                <div className="p-3 rounded-lg text-xs" style={{
                  backgroundColor: 'var(--cg-accent-soft)',
                  border: '1px solid var(--cg-accent)'
                }}>
                  <i className="fa-solid fa-info-circle mr-1"></i>
                  Este post será publicado como {platform === 'instagram' ? 'carrossel' : 'álbum'} com {generatedPost.imageUrls.length} imagens.
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4" style={{ borderTop: '1px solid var(--cg-border)' }}>
              <button
                onClick={() => setShowPublishModal(false)}
                className="btn h-10 px-6"
                style={{ backgroundColor: 'var(--cg-bg-alt)' }}
                disabled={publishing}
              >
                Cancelar
              </button>
              <button
                onClick={handlePublish}
                disabled={!selectedAccountId || publishing}
                className="btn btn-primary h-10 px-6"
              >
                {publishing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Publicando...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-rocket mr-2"></i>
                    Publicar Agora
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
